//
// Copyright (c) 2011-2022 by Curalate, Inc.
//

import { AxisLabelsFormatterContextObject } from 'highcharts';
import moment from 'moment-timezone';

import {
  ITikTokEngagementDailyStats,
  TIKTOK_API_DATE_FORMAT,
} from '@apis/tiktok-analytics/tiktokAnalyticsApiModel';
import DateString from '@models/DateString';
import TimeZoneId from '@models/TimeZoneId';
import {
  BASE_CHART_OPTIONS,
  ENGAGEMENT_EMPTY_DAILY_METRICS,
  TikTokEngagementPerformanceOption,
} from '@tiktok/components/analytics/engagement/tiktokEngagementConstants';
import { getDatesInRange } from '@utils/getDatesInRange';
import { normalizeToMap } from '@utils/normalize';
import {
  commaSeparatedNumber,
  readableNumber,
  readableRate,
} from '@utils/readableNumber';

import {
  DECIMAL_CUSTOM_OPTIONS,
  NUMBER_CUSTOM_OPTIONS,
} from 'app/constants/highcharts-custom-options';

export const getValueChartOptions = (
  currentMetric: TikTokEngagementPerformanceOption,
): Highcharts.Options => {
  const predefinedCustomOptions =
    currentMetric === TikTokEngagementPerformanceOption.EngagementRate
      ? DECIMAL_CUSTOM_OPTIONS((n: number) =>
          readableRate(n * 100, { rateIncludePercentSuffix: false }),
        )
      : {
          ...NUMBER_CUSTOM_OPTIONS(commaSeparatedNumber),
          yAxis: {
            labels: {
              formatter: function (
                this: AxisLabelsFormatterContextObject<number>,
              ) {
                return readableNumber(this.value);
              },
            },
          },
        };

  return {
    ...predefinedCustomOptions,
    plotOptions: {},
    ...BASE_CHART_OPTIONS,
  };
};

const getFormattedDate = (d: Date | string) => moment(d).format('YYYY-MM-DD');

export function fillInMissingEngagementDates(
  days: ITikTokEngagementDailyStats[],
  start: DateString,
  end: DateString,
  timezone: TimeZoneId,
): ITikTokEngagementDailyStats[] {
  const { byId: existingDaysMap } = normalizeToMap(days, (day) =>
    getFormattedDate(DateString.fromString(day.date, TIKTOK_API_DATE_FORMAT)),
  );

  const startDate = DateString.toDate(start, timezone);
  const endDate = DateString.toDate(end, timezone);

  return getDatesInRange(startDate, endDate, { inclusive: true }).map(
    (date) => {
      const dateKey = getFormattedDate(date);
      const existingDay = existingDaysMap.get(dateKey);
      return existingDay
        ? {
            ...existingDay,
            date: DateString.fromString(
              existingDay.date,
              TIKTOK_API_DATE_FORMAT,
            ),
          }
        : {
            date: DateString.fromString(dateKey, TIKTOK_API_DATE_FORMAT),
            metrics: ENGAGEMENT_EMPTY_DAILY_METRICS,
          };
    },
  );
}
